import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import AlbumCard from "@/components/AlbumCard";
import TrackRow from "@/components/TrackRow";
import { Button } from "@/components/ui/button";
import { Play, Crown, Zap } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1519744792095-2f2205e87b6f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Home() {
  const [albums, setAlbums] = useState([]);
  const [tracks, setTracks] = useState([]);
  const { isPremium, isGuest } = useAuth();
  const { playTrack } = usePlayer();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/albums").then((r) => setAlbums(r.data)).catch(() => {});
    api.get("/tracks").then((r) => setTracks(r.data)).catch(() => {});
  }, []);

  const trending = [...tracks].sort((a, b) => (b.plays || 0) - (a.plays || 0)).slice(0, 6);

  return (
    <div>
      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 to-transparent" />
        </div>
        <div className="relative px-6 md:px-12 py-16 md:py-28 max-w-3xl">
          <div className="overline text-xs text-accent mb-4 flex items-center gap-2"><Zap size={14} /> Underground · No Skips · No Ops</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tight mb-4">
            FUCK<br />THE <span className="text-primary">OPS.</span>
          </h1>
          <p className="font-display text-lg md:text-xl text-white/90 mb-4 max-w-lg">This is gang gang music.</p>
          <p className="text-sm md:text-base text-muted-foreground mb-8 max-w-lg">
            Raw drops, full albums, exclusive tracks. Guests preview 25 seconds — go premium for unlimited full-length listening at $5/month.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button data-testid="hero-browse" onClick={() => nav("/browse")} className="bg-primary hover:bg-white hover:text-primary rounded-none font-bold overline text-sm h-12 px-6">
              <Play size={18} className="mr-2" /> Start Listening
            </Button>
            {!isPremium && (
              <Button data-testid="hero-premium" onClick={() => nav(isGuest ? "/register" : "/subscribe")} variant="outline" className="rounded-none border-2 border-accent text-accent hover:bg-accent hover:text-black font-bold overline text-sm h-12 px-6">
                <Crown size={18} className="mr-2" /> Go Premium
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* TRENDING */}
      {trending.length > 0 && (
        <section className="px-6 md:px-12 py-10">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-display text-lg md:text-lg font-bold overline tracking-widest">Trending Now</h2>
          </div>
          <div className="border-t border-border">
            {trending.map((t, i) => <TrackRow key={t.id} track={t} index={i} list={trending} />)}
          </div>
        </section>
      )}

      {/* ALBUMS */}
      <section className="px-6 md:px-12 py-10">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-lg md:text-lg font-bold overline tracking-widest">Latest Drops</h2>
          <button data-testid="see-all-albums" onClick={() => nav("/browse")} className="text-xs overline text-muted-foreground hover:text-primary transition-colors">See All →</button>
        </div>
        {albums.length === 0 ? (
          <div className="border-2 border-dashed border-border p-12 text-center text-muted-foreground text-sm">
            No drops yet. {isGuest ? "" : "Admins can upload from the Admin panel."}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {albums.slice(0, 10).map((a) => <AlbumCard key={a.id} album={a} />)}
          </div>
        )}
      </section>
    </div>
  );
}
